import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { cn } from 'lib/utils';
import { Skeleton } from 'components/ui/skeleton';
import { PeriodBadge, PERIOD_DOT } from 'components/AvailabilityBadge';
import { apiErrorMessage } from 'utils/errorMessage';
import { formatPeriod, periodMeta } from 'utils/unavailability';
import type { UnavailabilityPeriod } from 'api/unavailabilitiesApi';

/**
 * The technician's declared periods, with the four states a list can be in.
 *
 * One component for the profile screen and for the ticket screen's sheet: two copies would be two
 * chances for one of them to keep showing a cross on a period the other has already made
 * undeletable, or to answer a failed read with "no period declared" — which is the same lie a
 * green badge would be.
 */

const ROW = 'border-t border-zinc-200 dark:border-zinc-800';
/** 44 px, the smallest target a thumb hits reliably — the mock-up draws 30. */
const TAP = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl';

interface UnavailabilityPeriodListProps {
  periods: UnavailabilityPeriod[];
  isLoading: boolean;
  error: unknown;
  onDelete: (period: UnavailabilityPeriod) => void;
}

const UnavailabilityPeriodList = ({
  periods,
  isLoading,
  error,
  onDelete,
}: UnavailabilityPeriodListProps) => {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className={cn('space-y-2 p-4', ROW)}>
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  // "No period declared" on a failed read claims the list is empty, which is not what is known.
  if (error) {
    return (
      <p className={cn('px-4 py-4 text-sm leading-relaxed text-red-700 dark:text-red-300', ROW)}>
        {apiErrorMessage(t, error)}
      </p>
    );
  }

  if (periods.length === 0) {
    return (
      <p className={cn('px-4 py-4 text-sm leading-relaxed text-zinc-400 dark:text-zinc-500', ROW)}>
        {t('No period declared. You are proposed for every intervention in your scope.')}
      </p>
    );
  }

  return (
    <ul>
      {periods.map((period) => (
        <li key={period.id} className={cn('flex items-center gap-3 px-4 py-2.5', ROW)}>
          <span
            className={cn('h-2.5 w-2.5 shrink-0 rounded-full', PERIOD_DOT[period.state])}
            aria-hidden="true"
          />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[13px] font-medium text-zinc-900 dark:text-zinc-50">
              {formatPeriod(period.startDate, period.endDate)}
            </p>
            <p className="truncate text-xs text-zinc-500 dark:text-zinc-400">
              {periodMeta(t, period)}
            </p>
          </div>
          <PeriodBadge state={period.state} />
          {/* No cross on a period that is over: it can no longer be deleted (D10), and the
              backoffice has no write path to put back what would have been erased. */}
          {period.state !== 'past' ? (
            <button
              type="button"
              onClick={() => onDelete(period)}
              aria-label={t('Delete this period')}
              className={cn(TAP, '-mr-2 text-zinc-400 active:text-rose-600 dark:text-zinc-500')}
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : (
            // The mock-up's spacer, and not decoration: without it the badge of a period that is
            // over slides into the column the crosses occupy, and the two rows stop lining up.
            <span className={cn(TAP, '-mr-2')} aria-hidden="true" />
          )}
        </li>
      ))}
    </ul>
  );
};

export default UnavailabilityPeriodList;
